import React, { useCallback, useState } from 'react'
import { ReactComponent as PdfIcon } from '../../../assets/icons/pdf.svg'
import s from './index.module.scss'
import DetailModalWrapper from '../../../components/DetailModalWrapper'
import SimpleTable from '../../../components/UI/SimpleTable'
import Accordion from '../../../components/UI/Accordion'
import Table from '../../../components/Table'
import CreateModal from '../../../components/CreateModal'
import {
	useChangeLegalPersonStatusMutation,
	useEditLegalPersonMutation,
	useGetLegalPersonByIdQuery,
	useGetLegalPersonPaymentsQuery,
	useToggleDiscountMutation,
} from '../../../store/legalPersons/legalPersons.api'
import { useGetAddressesQuery, useGetClientOrdersQuery } from '../../../store/api'
import { addressesColumns, ordersColumns } from '../../../constants/clientsPage'
import { legalPersonPaymentsColumns, legalPersonsInputs, legalPersonsInputsData } from '../../../constants/legalPersonsPage'
import { fopsInputsData } from '../../../constants/fopsPage'
import { reconciliationSchema, schema } from './schema'
import { exportHandler, formatPhone } from '../../../helpers'
import dateFormat from 'dateformat'
import classNames from 'classnames'
import TableDateFilter from '../../../components/UI/TableDateFilter'
import Button from '../../../components/UI/Button'
import discountActive from '../../../assets/images/discount-active-mini.webp'
import discountInactive from '../../../assets/images/discount-inactive-mini.webp'
import { useAppSelector } from '../../../hooks/useAppSelector'
import Input from '../../../components/UI/Input'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import Region from '../../../components/UI/Region'
import { useActions } from '../../../hooks/useActions'

interface IProps {
	id: string | number
	closeHandler: () => void
	isFop?: boolean
}

const LegalPersonsDetailModal: React.FC<IProps> = React.memo(({ id, closeHandler, isFop }) => {
	const { permissions } = useAppSelector((state) => state.common)

	const { setOrderId } = useActions()
	
	const [paymentsSort, setPaymentsSort] = useState({})
	const [ordersSort, setOrdersSort] = useState({})
	const [paymentsPerPage, setPaymentsPerPage] = useState<number>(30)
	const [ordersPerPage, setOrdersPerPage] = useState<number>(30)
	const [editModalIsVisible, setEditModalIsVisible] = useState<boolean>(false)
	const [paymentsDateFrom, setPaymentsDateFrom] = useState('')
	const [paymentsDateTo, setPaymentsDateTo] = useState('')
	const [ordersDateFrom, setOrdersDateFrom] = useState('')
	const [ordersDateTo, setOrdersDateTo] = useState('')

	const paymentsDateQuery = {
		...(paymentsDateFrom && { dateFrom: paymentsDateFrom }),
		...(paymentsDateTo && { dateTo: paymentsDateTo }),
	}

	const exportPaymentsQuery = Object.entries(paymentsDateQuery).reduce((prev, [key, value], index) => `${prev}${index > 0 ? '&' : ''}${key}=${value}`, '')

	const ordersDateQuery = {
		...(ordersDateFrom && { dateFrom: ordersDateFrom }),
		...(ordersDateTo && { dateTo: ordersDateTo }),
	}

	const exportOrdersQuery = Object.entries(ordersDateQuery).reduce((prev, [key, value], index) => `${prev}${index > 0 ? '&' : ''}${key}=${value}`, '')

	const { data, isLoading } = useGetLegalPersonByIdQuery({ id })
	const {
		data: payments,
		isLoading: paymentsLoading,
		isFetching: paymentsFetching,
	} = useGetLegalPersonPaymentsQuery({
		id,
		perPage: paymentsPerPage,
		...paymentsSort,
		...paymentsDateQuery,
	})
	const {
		data: orders,
		isLoading: ordersLoading,
		isFetching: ordersFetching,
	} = useGetClientOrdersQuery(
		{
			userId: data?.userId,
			perPage: ordersPerPage,
			...ordersSort,
			...ordersDateQuery,
		},
		{ skip: !data?.userId },
	)
	const { data: addresses, isLoading: addressesLoading } = useGetAddressesQuery({ id: data?.userId }, { skip: !data?.userId })
	const [editFunc, { error, isLoading: isEditing }] = useEditLegalPersonMutation()
	const [changeStatus] = useChangeLegalPersonStatusMutation()
	const [toggleDiscount] = useToggleDiscountMutation()

	const detailInfo = [
		{ title: 'ЄДРПОУ', value: data?.code || '' },
		{ title: 'Адреса', value: data?.address || '' },
		{ title: 'Керівник', value: data?.directorFio || '' },
		{ title: 'IBAN', value: data?.iban || '' },
		{ title: 'E-mail', value: data?.directorEmail || '' },
		{
			title: 'Номер телефону',
			value: data?.directorPhone ? (
				<a className={s.link} href={`tel:${data?.directorPhone.replaceAll('+', '')}`}>
					{formatPhone(data?.directorPhone)}
				</a>
			) : (
				''
			),
		},
		{
			title: 'Дата реєстрації',
			value: data?.registeredAt ? dateFormat(data.registeredAt, 'dd.mm.yyyy') : '',
		},
		{ title: 'Банк', value: data?.bank || '' },
		{ title: 'Платник ПДВ', value: data?.vatPayer ? 'Так' : 'Ні' },
		{
			title: 'Платник акцизного податку на пальне',
			value: data?.exciseDutyPayer ? 'Так' : 'Ні',
		},
		{
			title: 'Ліцензія на зберігання пального',
			value: data?.fuelStorageLicense ? 'Так' : 'Ні',
		},
		{
			title: 'Граничне значення на балансі',
			value: (data?.lowerLimit || 0).toFixed(2),
		},
	]

	const fopDetailInfo = [
		{
			title: 'Номер телефону',
			value: data?.directorPhone ? (
				<a className={s.link} href={`tel:${data?.directorPhone.replaceAll('+', '')}`}>
					{formatPhone(data?.directorPhone)}
				</a>
			) : (
				''
			),
		},
		{ title: 'E-mail', value: data?.directorEmail || '' },
		{
			title: 'Дата реєстрації',
			value: data?.registeredAt ? dateFormat(data.registeredAt, 'dd.mm.yyyy') : '',
		},
		{ title: 'РНОКПП', value: data?.code || '' },
		{ title: 'Адреса', value: data?.address || '' },
		{
			title: 'Дата народження',
			value: data?.directorBirthdate ? dateFormat(data.directorBirthdate, 'dd.mm.yyyy') : '',
		},
		{ title: 'Банк', value: data?.bank || '' },
		{ title: 'Платник ПДВ', value: data?.vatPayer ? 'Так' : 'Ні' },
		{
			title: 'Платник акцизного податку на пальне',
			value: data?.exciseDutyPayer ? 'Так' : 'Ні',
		},
		{
			title: 'Ліцензія на зберігання пального',
			value: data?.fuelStorageLicense ? 'Так' : 'Ні',
		},
		{
			title: 'Граничне значення на балансі',
			value: (data?.lowerLimit || 0).toFixed(2),
		},
	]

	const changePaymentsSort = useCallback((name: string, value: string) => {
		setPaymentsSort({ [`sort[${name}]`]: value })
	}, [])

	const changeOrdersSort = useCallback((name: string, value: string) => {
		setOrdersSort({ [`sort[${name}]`]: value })
	}, [])

	const fetchPayments = useCallback(() => {
		if (payments?.total && paymentsPerPage < payments.total && !paymentsFetching) {
			setPaymentsPerPage((prev) => prev + 30)
		}
	}, [payments?.total, paymentsFetching])

	const fetchOrders = useCallback(() => {
		if (orders?.total && ordersPerPage < orders.total && !ordersFetching) {
			setOrdersPerPage((prev) => prev + 30)
		}
	}, [orders?.total, ordersFetching])

	const editHandler = (formData: legalPersonsInputs) => {
		formData.directorBirthdate = formData?.directorBirthdate ? dateFormat(formData.directorBirthdate, 'yyyy-mm-dd') : ''
		formData.overdraftAmount = Number(formData?.overdraftAmount) * 100
		formData.lowerLimit = Number(formData?.lowerLimit) * 100
		formData.discountPerLiter = Number(formData?.discountPerLiter) * 100

		editFunc({ id, data: { ...formData, regionId: data?.region?.id || 0} })
			.unwrap()
			.then(() => setEditModalIsVisible(false))
	}

	const blockHandler = () => {
		changeStatus({ id })
	}

	return (
		<DetailModalWrapper
			isLoading={isLoading || addressesLoading || paymentsLoading || ordersLoading}
			closeHandler={closeHandler}
			headerTitle={
				<span
					className={classNames(s.title, {
						[s.titlePadding]: data?.discountCard,
					})}
				>
					{data?.name}
				</span>
			}
			headerSubtitle={
				<span className={classNames({ [s.titlePadding]: data?.discountCard })}>
					{permissions.includes('CLIENTS_UPDATE') && data?.discountCard && (
						<span
							className={classNames(s.icon, {
								[s.iconActive]: data?.discountCardConfirmed,
							})}
							onClick={() => toggleDiscount({ id })}
						>
							<img src={data?.discountCardConfirmed ? discountActive : discountInactive} alt="Icon" />
						</span>
					)}
					ID {data?.userId}
				</span>
			}
			isReversedTitles
			status={data?.status}
			deleted={data?.deleted}
			editHandler={() => setEditModalIsVisible(true)}
			blockHandler={blockHandler}
			disableEdit={!permissions.includes('CLIENTS_UPDATE')}
		>
			<div
				className={classNames(s.balance, {
					[s.titlePadding]: data?.discountCard,
				})}
			>
				<div>
					Баланс <span>{((data?.balance || 0) / 100).toFixed(2)}</span> грн
				</div>
				<div>
					Овердрафт <span>{(data?.overdraftAmount || 0).toFixed(2)}</span> грн
				</div>
				<div>
					Знижка <span>{(data?.discountPerLiter || 0).toFixed(2)}</span> грн/л
				</div>
			</div>

			<Region
				region={data?.region?.name}
				className={data?.discountCard ? s.titlePadding : ''}
			/>

			<DetailModalWrapper.Title>{isFop ? 'Персональна інформація' : 'Інформація про компанію'}</DetailModalWrapper.Title>
			<SimpleTable data={isFop ? fopDetailInfo : detailInfo} />

			<DetailModalWrapper.Title>Акт звірки</DetailModalWrapper.Title>
			<ReconciliationForm id={data?.userId || 0} name={data?.name || ''} />

			<DetailModalWrapper.Title>Договір</DetailModalWrapper.Title>
			<div className={s.flexBlock}>
				<span onClick={() => exportHandler(`legal-persons/${data?.entityId}/contract`, `Договір №${data?.entityId}`, 'pdf')}>
					<PdfIcon /> договір №{data?.contractNumber}
				</span>
				дійсний до {data?.contractEndAt && dateFormat(data.contractEndAt, 'dd.mm.yyyy')}
			</div>

			<Accordion title={'Історія замовлень'}>
				<div className={s.wrapper}>
					<TableDateFilter setDateFrom={setOrdersDateFrom} setDateTo={setOrdersDateTo} />
					<Button variant={'export'} onClick={() => exportHandler(`users/${data?.userId}/orders/export?${exportOrdersQuery}`, 'Замовлення')} />
				</div>
				<Table data={orders?.data || []} columns={ordersColumns} maxHeight={'500px'} sortHandler={changeOrdersSort} onDynamicFetch={fetchOrders} showDetail={setOrderId} />
			</Accordion>
			<Accordion title={'Історія оплат'}>
				<div className={s.wrapper}>
					<TableDateFilter setDateFrom={setPaymentsDateFrom} setDateTo={setPaymentsDateTo} />
					<Button variant={'export'} onClick={() => exportHandler(`legal-persons/${id}/payments/export?${exportPaymentsQuery}`, 'Оплати')} />
				</div>
				<Table data={payments?.data || []} columns={legalPersonPaymentsColumns} maxHeight={'500px'} sortHandler={changePaymentsSort} onDynamicFetch={fetchPayments} />
			</Accordion>
			<Accordion title={'Адреси'}>
				<Table data={addresses?.data || []} columns={addressesColumns} maxHeight={'500px'} />
			</Accordion>

			{editModalIsVisible && (
				<CreateModal<legalPersonsInputs>
					title={isFop ? 'Редагувати ФОП' : 'Редагувати компанію'}
					inputs={isFop ? fopsInputsData : legalPersonsInputsData}
					schema={schema}
					initialData={data}
					isLoading={isEditing}
					error={error && 'data' in error ? error.data.message : ''}
					onSubmit={editHandler}
					closeHandler={() => setEditModalIsVisible(false)}
				/>
			)}
		</DetailModalWrapper>
	)
})

export default LegalPersonsDetailModal

const ReconciliationForm = ({ id, name }: { id: string | number; name: string }) => {
  const { 
		register, 
		handleSubmit, 
		formState: { errors } 
	} = useForm<{ dateFrom: string; dateTo: string }>({
		values: { dateFrom: '', dateTo: '' },
    resolver: yupResolver(reconciliationSchema)
  })

  const onSubmit = (data: { dateFrom: string; dateTo: string }) => {
    const dateFrom = dateFormat(data.dateFrom, 'yyyy-mm-dd')
    const dateTo = dateFormat(data.dateTo, 'yyyy-mm-dd')

    exportHandler(
      `reconciliation-act?pdfMode=3&timeZone=Europe/Kyiv&userId=${id}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
      `Акт звірки для ${name} за ${dateFrom}-${dateTo}`, 
      'pdf', 
      true
    )
  }

  return (
    <form 
			className={s.reconciliationWrapper} 
			onSubmit={handleSubmit(onSubmit)}
		>
			<Input
				label="Дата з"
				type="date"
				register={register}
				name="dateFrom"
				errors={errors}
			/>
			<Input
				label="Дата до(включно)"
				type="date"
				register={register}
				name="dateTo"
				errors={errors}
				className={s.reconciliationInput}
			/>
      <Button
        variant="filled"
        type="submit"
        className={s.reconciliationBtn}
      >
        Сформувати
      </Button>
    </form>
  )
}
